import React, { useEffect, useState, Suspense, lazy, createElement } from 'react';
import { SalesHero } from './components/SalesHero';
import { CartProvider } from './components/CartContext';
// Lazy load below-the-fold components
const InstantDelivery = lazy(() => import('./components/InstantDelivery').then(m => ({
  default: m.InstantDelivery
})));
const Testimonials = lazy(() => import('./components/Testimonials').then(m => ({
  default: m.Testimonials
})));
const SalesContent = lazy(() => import('./components/SalesContent').then(m => ({
  default: m.SalesContent
})));
const SalesOffer = lazy(() => import('./components/SalesOffer').then(m => ({
  default: m.SalesOffer
})));
const SalesOfferBottom = lazy(() => import('./components/SalesOfferBottom').then(m => ({
  default: m.SalesOfferBottom
})));
const Footer = lazy(() => import('./components/Footer').then(m => ({
  default: m.Footer
})));
const RecentPurchaseToast = lazy(() => import('./components/RecentPurchaseToast').then(m => ({
  default: m.RecentPurchaseToast
})));
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: (...args: any[]) => void;
  }
}
const PIXEL_ID = '1436890408218834';
function initFacebookPixel() {
  if (window.fbq) return;
  const fbq = function (...args: any[]) {
    if ((fbq as any).callMethod) {
      ;
      (fbq as any).callMethod.apply(fbq, args);
    } else {
      ;
      (fbq as any).queue.push(args);
    }
  } as any;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [] as any[];
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
  window.fbq('init', PIXEL_ID);
  window.fbq('track', 'PageView');
}
// Add preconnect hints for external domains
function addPreconnectHints() {
  const domains = ['https://cdn.magicpatterns.com', 'https://connect.facebook.net', 'https://coresolutionsg.com', 'https://fonts.googleapis.com', 'https://fonts.gstatic.com'];
  domains.forEach(href => {
    if (!document.querySelector(`link[href="${href}"]`)) {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    }
  });
  // Load Google Font asynchronously (non-render-blocking)
  const fontUrl = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap';
  if (!document.querySelector(`link[href="${fontUrl}"]`)) {
    const fontLink = document.createElement('link');
    fontLink.rel = 'stylesheet';
    fontLink.href = fontUrl;
    fontLink.media = 'print';
    fontLink.onload = function () {
      ;
      (this as HTMLLinkElement).media = 'all';
    };
    document.head.appendChild(fontLink);
  }
  // Preload hero image so it's ready before React renders
  const heroImg = 'https://cdn.magicpatterns.com/uploads/wppacRyZC2Ww6zkTNvLKh7/FREELANCE_NEW_COVER.png';
  if (!document.querySelector(`link[href="${heroImg}"]`)) {
    const preload = document.createElement('link');
    preload.rel = 'preload';
    preload.as = 'image';
    preload.href = heroImg;
    document.head.appendChild(preload);
  }
}
// Inject Open Graph meta tags so Instagram/Facebook show the correct preview
function injectMetaTags() {
  // Set page title
  document.title = 'Freelance Desde Cero (Sin Inglés) — Tu Primer Cliente en 7 Días';
  // Set meta description
  const metaDesc = document.querySelector('meta[name="description"]');
  if (metaDesc) {
    metaDesc.setAttribute('content', 'El mapa completo para conseguir tu primer cliente freelance en español. Sin inglés, sin experiencia, sin título. Acceso inmediato por $14 USD.');
  } else {
    const desc = document.createElement('meta');
    desc.name = 'description';
    desc.content = 'El mapa completo para conseguir tu primer cliente freelance en español. Sin inglés, sin experiencia, sin título. Acceso inmediato por $14 USD.';
    document.head.appendChild(desc);
  }
  // Open Graph tags for Instagram/Facebook link previews
  const ogTags: Record<string, string> = {
    'og:title': 'Freelance Desde Cero (Sin Inglés) — $14 USD',
    'og:description': 'El mapa paso a paso para conseguir tu primer cliente freelance en español. Sin inglés, sin experiencia. +1,200 mujeres latinas ya lo tienen.',
    'og:image': 'https://cdn.magicpatterns.com/uploads/wppacRyZC2Ww6zkTNvLKh7/FREELANCE_NEW_COVER.png',
    'og:image:width': '1200',
    'og:image:height': '1200',
    'og:type': 'product',
    'og:site_name': 'Freelance Desde Cero',
    'og:locale': 'es_ES'
  };
  Object.entries(ogTags).forEach(([property, content]) => {
    const existing = document.querySelector(`meta[property="${property}"]`);
    if (existing) {
      existing.setAttribute('content', content);
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', property);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
    }
  });
  // Twitter Card tags (also used by some platforms)
  const twitterTags: Record<string, string> = {
    'twitter:card': 'summary_large_image',
    'twitter:title': 'Freelance Desde Cero (Sin Inglés) — $14 USD',
    'twitter:description': 'El mapa paso a paso para conseguir tu primer cliente freelance en español. Sin inglés, sin experiencia.',
    'twitter:image': 'https://cdn.magicpatterns.com/uploads/wppacRyZC2Ww6zkTNvLKh7/FREELANCE_NEW_COVER.png'
  };
  Object.entries(twitterTags).forEach(([name, content]) => {
    const existing = document.querySelector(`meta[name="${name}"]`);
    if (existing) {
      existing.setAttribute('content', content);
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('name', name);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
    }
  });
}
// Minimal placeholder while lazy sections load
function SectionLoader() {
  return <div className="py-12 px-4 bg-[#faf6f1]" data-id="element-0" />;
}
export function App() {
  useEffect(() => {
    injectMetaTags();
    addPreconnectHints();
    if ('requestIdleCallback' in window) {
      ;
      (window as any).requestIdleCallback(() => initFacebookPixel(), {
        timeout: 3000
      });
    } else {
      setTimeout(initFacebookPixel, 2000);
    }
  }, []);
  return <CartProvider data-id="element-1">
      <div className="min-h-screen font-sans antialiased overflow-x-hidden bg-[#faf6f1]" data-id="element-2">
        {/* Above the fold — eagerly loaded, always visible */}
        <SalesHero data-id="element-3" />

        {/* Below fold — grouped into 2 Suspense boundaries to reduce spinners */}
        <Suspense fallback={<SectionLoader data-id="element-5" />} data-id="element-4">
          <InstantDelivery data-id="element-6" />
          <Testimonials data-id="element-7" />
          <SalesContent data-id="element-8" />
        </Suspense>

        <Suspense fallback={<SectionLoader data-id="element-10" />} data-id="element-9">
          <SalesOffer data-id="element-11" />
          <SalesOfferBottom data-id="element-12" />
          <Footer data-id="element-13" />
        </Suspense>

        {/* Toast — deferred, not critical */}
        <DeferredToast data-id="element-14" />
      </div>
    </CartProvider>;
}
// Defer toast by 5 seconds so it doesn't compete with initial load
function DeferredToast() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setShow(true), 5000);
    return () => clearTimeout(timer);
  }, []);
  if (!show) return null;
  return <Suspense fallback={null} data-id="element-15">
      <RecentPurchaseToast data-id="element-16" />
    </Suspense>;
}